/**
 * OneBase Líneas de Negocio
 * Página de gestión de líneas de negocio del ERP OneBase
 */

import React, { useState, useEffect } from 'react';
import { lineaNegocioService } from '../services/onebaseService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiBriefcase } from 'react-icons/fi';
import '../styles/onebase-lineas-negocio.css';

import Loading from '../components/Loading';
const OneBaseLineasNegocio = () => {
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedLinea, setSelectedLinea] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
  });

  useEffect(() => {
    cargarLineasNegocio();
  }, []);

  const cargarLineasNegocio = async () => {
    setLoading(true);
    try {
      const response = await lineaNegocioService.getAll({ status: 'activo' });
      const lineasData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setLineasNegocio(lineasData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar líneas de negocio');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedLinea) {
        await lineaNegocioService.update(selectedLinea.id, formData);
        toast.success('Línea de negocio actualizada');
      } else {
        await lineaNegocioService.create(formData);
        toast.success('Línea de negocio creada');
      }
      setShowForm(false);
      resetForm();
      cargarLineasNegocio();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.non_field_errors?.[0] || 'Error al guardar línea de negocio');
    }
  };

  const handleEdit = (linea) => {
    setSelectedLinea(linea);
    setFormData({
      nombre: linea.nombre,
      descripcion: linea.descripcion || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta línea de negocio?')) {
      try {
        await lineaNegocioService.delete(id);
        toast.success('Línea de negocio eliminada');
        cargarLineasNegocio();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
    });
    setSelectedLinea(null);
  };

  const filteredLineas = lineasNegocio.filter(
    (l) => l.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="onebase-lineas-negocio">
      <div className="page-header">
        <h1>Líneas de Negocio</h1>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FiPlus /> Nueva Línea de Negocio
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar por nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <Loading fullScreen={false} />
      ) : filteredLineas.length > 0 ? (
        <div className="lineas-grid">
          {filteredLineas.map((linea) => (
            <div key={linea.id} className="linea-card">
              <div className="card-header">
                <div className="card-icon">
                  <FiBriefcase size={24} />
                </div>
                <div className="card-title">
                  <h3>{linea.nombre}</h3>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(linea)}
                    title="Editar"
                  >
                    <FiEdit />
                  </button>
                  <button
                    className="btn-icon danger"
                    onClick={() => handleDelete(linea.id)}
                    title="Eliminar"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {linea.descripcion && (
                <div className="card-body">
                  <p>{linea.descripcion}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FiBriefcase size={64} />
          <p>No hay líneas de negocio</p>
          <button
            className="estado-btn"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Crear primera línea de negocio
          </button>
        </div>
      )}

      {/* Modal de formulario */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedLinea ? 'Editar Línea de Negocio' : 'Nueva Línea de Negocio'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  placeholder="Ej: Publicidad, Audiovisual, Eventos..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Descripción de la línea de negocio..."
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {selectedLinea ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OneBaseLineasNegocio;
